/**
 * BOARD interior system — the room behind the BOARD building (plot P1).
 *
 * Builds an enclosed room on its own island slot (BOARD_ISLAND_Z) whose north
 * wall is a five-column KANBAN wall of the org's task backlog. Each task is a
 * framed card (gallery-style: charcoal frame + priority-coloured inner panel,
 * red corner tag when blocked) placed in its status column. DOM mini-cards show
 * the task detail on approach. An actor may launch only cards carrying Portal's
 * exact generic `launch` action; status transitions remain read-only.
 *
 * Everything authored here is parented to the boardRoot entity passed to init —
 * never the global worldRoot (which stays pointed at the office for the whole
 * session) — so the two interiors never write into each other's coordinate space.
 * Room-static colliders are registered once during init, while the caller has
 * the board island's collider placement set.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type { Task, TaskStatus } from '../../api/types'
import { INPUT_PRIORITY, modalInputRouter } from '../../input/modalInputRouter'
import { injectWorldCardStyles } from './labelStyles'
import { computeLabelScreenPos } from './labels'
import { BOARD_ISLAND_Z } from './interiors'
import {
  buildStandardInteriorShell,
  createWorldOverlay,
  escapeWorldHtml,
  type OverlayHandle,
} from './interiorShared'
import { createInteriorMaterial, InteriorDressingKit } from './interiorDressing'
import {
  BOARD_HALF_X, BOARD_HALF_Z, BOARD_WALL_HEIGHT,
  BOARD_CARD_W, BOARD_CARD_H,
  BOARD_BANNER_W, BOARD_BANNER_H,
  BOARD_COLUMNS, BOARD_COLUMN_LABELS,
  BOARD_STRATEGY_ROOM,
  boardBannerPosition,
  computeBoardWalls, selectBoardLaunchTarget,
  layoutBoardCards, boardPriorityColor, normalizeTaskStatus,
  type BoardCard,
} from './board'

/** Show a board card's DOM detail within this distance (world units). */
const CARD_LABEL_DIST = 9
/** How often the backlog is polled while the player is inside the room. */

const CHARCOAL = new pc.Color(0.239, 0.251, 0.357)

/** DOM status-pill colours for the five board columns. */
const STATUS_HEX: Record<TaskStatus, string> = {
  'ready': '#94a3b8',
  'in-progress': '#a78bfa',
  'blocked': '#f87171',
  'done': '#22d3ee',
  'cancelled': '#6b7280',
}

interface CardEntry {
  card: BoardCard
  root: pc.Entity
}

export class BoardSystem {
  private app!: pc.Application
  private root!: pc.Entity
  private cameraEntity!: pc.Entity

  private container!: HTMLDivElement
  private overlay: OverlayHandle | null = null
  private cardLabels = new Map<string, HTMLDivElement>()
  private tmpVec = new pc.Vec3()

  private frameMat!: pc.StandardMaterial
  private blockedTagMat!: pc.StandardMaterial
  private panelMats = new Map<string, pc.StandardMaterial>()

  private tasks: Task[] = []
  private taskById = new Map<string, Task>()
  private cardEntries: CardEntry[] = []
  private cardsHolder!: pc.Entity
  private taskWallBatchGroupId: number | undefined

  private active = false
  private nearestLaunchTaskId: string | null = null
  private unsubscribe?: () => void
  private disposeInput?: () => void

  init(app: pc.Application, root: pc.Entity, cameraEntity: pc.Entity): void {
    this.app = app
    this.root = root
    this.cameraEntity = cameraEntity

    this.frameMat = new pc.StandardMaterial()
    this.frameMat.diffuse = CHARCOAL
    this.frameMat.gloss = 0.45
    this.frameMat.update()

    this.blockedTagMat = new pc.StandardMaterial()
    this.blockedTagMat.diffuse = new pc.Color(0.24, 0.06, 0.06)
    this.blockedTagMat.emissive = new pc.Color(0.97, 0.24, 0.24)
    this.blockedTagMat.emissiveIntensity = 3
    this.blockedTagMat.useLighting = false
    this.blockedTagMat.update()

    this.taskWallBatchGroupId = app.batcher
      ?.addGroup('board-task-wall', true, 32).id
    this.buildRoom()
    this.buildBanners()

    this.cardsHolder = new pc.Entity('board-cards')
    this.root.addChild(this.cardsHolder)

    // DOM overlay for card detail + [E] prompt (hidden until the player enters).
    injectWorldCardStyles()
    this.overlay = createWorldOverlay(app, { display: 'none' })
    this.container = this.overlay.element

    this.unsubscribe = useArenaStore.subscribe((state, prev) => {
      if (state.tasks === prev.tasks) return
      this.tasks = state.tasks
      this.taskById = new Map(state.tasks.map((task) => [task.id, task]))
      this.rebuildCards()
    })
    const tasks = useArenaStore.getState().tasks
    this.tasks = tasks
    this.taskById = new Map(tasks.map((task) => [task.id, task]))
    this.rebuildCards()
    this.disposeInput = modalInputRouter.register(INPUT_PRIORITY.room, this.onKeyDown)
  }

  // ── Static room geometry ────────────────────────────────────────────────────

  private buildRoom(): void {
    buildStandardInteriorShell({
      root: this.root,
      prefix: 'board',
      halfX: BOARD_HALF_X,
      halfZ: BOARD_HALF_Z,
      height: BOARD_WALL_HEIGHT,
      walls: computeBoardWalls(),
      floorColor: [0.19, 0.21, 0.24],
      backdropColor: [0.035, 0.045, 0.065],
      keyLightY: 2.8,
      keyLightRange: 40,
      accent: {
        name: 'wall-light',
        color: [0.75, 0.88, 1],
        y: 1.6,
        z: -BOARD_HALF_Z + 4,
        range: 22,
      },
    })

    const frame = createInteriorMaterial([0.08, 0.12, 0.18], { metalness: 0.58, gloss: 0.62 })
    const glass = createInteriorMaterial([0.23, 0.56, 0.72], { opacity: 0.2, gloss: 0.92 })
    const glow = createInteriorMaterial([0.34, 0.82, 0.94], { emissive: 1.7 })
    const strategyField = createInteriorMaterial([0.10, 0.21, 0.30], { emissive: 0.22 })
    const loungeField = createInteriorMaterial([0.16, 0.20, 0.22], { emissive: 0.12 })
    const reviewField = createInteriorMaterial([0.16, 0.18, 0.28], { emissive: 0.18 })
    const statusRail = createInteriorMaterial([0.25, 0.76, 0.92], { emissive: 2.2 })
    const dressing = new InteriorDressingKit(this.root)
    const add = (name: string, position: [number, number, number], scale: [number, number, number], mat: pc.Material) => {
      const entity = new pc.Entity(name)
      entity.addComponent('render', { type: 'box' })
      entity.setLocalPosition(...position)
      entity.setLocalScale(...scale)
      for (const mesh of entity.render?.meshInstances ?? []) mesh.material = mat
      this.root.addChild(entity)
    }

    // Three low material fields make the room program legible from the entry
    // without introducing another wall or narrowing the circulation path.
    add('board-zone-strategy', [0, 0.025, 1.2], [11, 0.05, 6.15], strategyField)
    add('board-zone-lounge', [-9.8, 0.025, 2.5], [5.1, 0.05, 5.4], loungeField)
    add('board-zone-review', [9.8, 0.025, 2.5], [5.1, 0.05, 5.4], reviewField)

    // The architectural layer frames licensed furniture only. Task truth stays
    // on the live wall; the glass suite, side programs and cyan datum provide
    // hierarchy without duplicating Portal-owned content.
    dressing.addGlassRoom(
      'board-strategy-room',
      BOARD_STRATEGY_ROOM.centerX,
      BOARD_STRATEGY_ROOM.centerZ,
      BOARD_STRATEGY_ROOM.width,
      BOARD_STRATEGY_ROOM.depth,
      glass,
      frame,
    )
    dressing.addCeilingRaft(
      'board-strategy-ceiling',
      BOARD_STRATEGY_ROOM.centerX,
      BOARD_STRATEGY_ROOM.centerZ,
      8.8,
      4.2,
      BOARD_WALL_HEIGHT,
      frame,
      glow,
    )
    dressing.addCeilingRaft('board-lounge-ceiling', -9.8, 2.5, 3.6, 3.4, BOARD_WALL_HEIGHT, frame, glow)
    dressing.addCeilingRaft('board-review-ceiling', 9.8, 2.5, 3.6, 3.4, BOARD_WALL_HEIGHT, frame, glow)
    add('board-task-wall-status-rail', [0, 0.28, -BOARD_HALF_Z + 0.36], [25.2, 0.07, 0.08], statusRail)
    for (const x of [-9.6, -4.8, 0, 4.8, 9.6]) {
      add(`board-task-wall-divider-${x}`, [x, 1.55, -BOARD_HALF_Z + 0.36], [0.035, 2.35, 0.08], frame)
    }
  }

  /** Canvas-textured column banner ("READY", "IN-PROGRESS", …) per column. */
  private buildBanners(): void {
    BOARD_COLUMNS.forEach((status, col) => {
      const [x, y, z] = boardBannerPosition(col)
      const banner = new pc.Entity(`board-banner-${status}`)
      banner.addComponent('render', { type: 'box' })
      banner.setLocalScale(BOARD_BANNER_W, BOARD_BANNER_H, 0.04)
      banner.setLocalPosition(x, y, z)

      const mat = new pc.StandardMaterial()
      mat.diffuse = new pc.Color(0.08, 0.09, 0.13)
      mat.emissive = new pc.Color(0.961, 0.922, 0.875)
      mat.emissiveIntensity = 0.5
      const hex = STATUS_HEX[status]
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 512
        canvas.height = 96
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.fillStyle = '#15181d'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.fillStyle = hex
          ctx.fillRect(0, 0, 12, canvas.height)
          ctx.fillStyle = '#f2ede4'
          ctx.font = '700 46px Nunito, system-ui, sans-serif'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.letterSpacing = '4px'
          ctx.fillText(BOARD_COLUMN_LABELS[status], canvas.width / 2 + 6, canvas.height / 2 + 2)
          const tex = new pc.Texture(this.app.graphicsDevice, { width: canvas.width, height: canvas.height })
          tex.setSource(canvas)
          mat.emissiveMap = tex
          mat.emissive = new pc.Color(1, 1, 1)
          mat.emissiveIntensity = 0.85
        }
      } catch { /* keep the plain emissive banner */ }
      mat.update()
      for (const mi of banner.render?.meshInstances ?? []) mi.material = mat
      this.root.addChild(banner)
    })
  }

  // ── Card meshes ─────────────────────────────────────────────────────────────

  private getPanelMat(priority?: string): pc.StandardMaterial {
    const key = priority?.toLowerCase() ?? '__default'
    const cached = this.panelMats.get(key)
    if (cached) return cached
    const c = boardPriorityColor(priority)
    const mat = new pc.StandardMaterial()
    mat.diffuse = new pc.Color(c[0] * 0.3, c[1] * 0.3, c[2] * 0.3)
    mat.emissive = new pc.Color(c[0], c[1], c[2])
    mat.emissiveIntensity = 0.5
    mat.gloss = 0.6
    mat.update()
    this.panelMats.set(key, mat)
    return mat
  }

  private rebuildCards(): void {
    for (const entry of this.cardEntries) entry.root.destroy()
    this.cardEntries = []

    const cards = layoutBoardCards(this.tasks)
    for (const card of cards) {
      const root = new pc.Entity(`board-card-${card.taskId}`)
      root.setLocalPosition(card.x, card.y, card.z)

      const frame = new pc.Entity('frame')
      frame.addComponent('render', { type: 'box' })
      if (frame.render && this.taskWallBatchGroupId !== undefined) {
        frame.render.batchGroupId = this.taskWallBatchGroupId
      }
      frame.setLocalScale(BOARD_CARD_W, BOARD_CARD_H, 0.05)
      for (const mi of frame.render?.meshInstances ?? []) mi.material = this.frameMat
      root.addChild(frame)

      const panel = new pc.Entity('panel')
      panel.addComponent('render', { type: 'box' })
      if (panel.render && this.taskWallBatchGroupId !== undefined) {
        panel.render.batchGroupId = this.taskWallBatchGroupId
      }
      panel.setLocalScale(BOARD_CARD_W - 0.1, BOARD_CARD_H - 0.1, 0.02)
      panel.setLocalPosition(0, 0, 0.03)
      const panelMat = this.getPanelMat(card.priority)
      for (const mi of panel.render?.meshInstances ?? []) mi.material = panelMat
      root.addChild(panel)

      // Red corner tag for blocked cards (top-right).
      if (card.blocked) {
        const tag = new pc.Entity('blocked-tag')
        tag.addComponent('render', { type: 'box' })
        if (tag.render && this.taskWallBatchGroupId !== undefined) {
          tag.render.batchGroupId = this.taskWallBatchGroupId
        }
        tag.setLocalScale(0.16, 0.16, 0.03)
        tag.setLocalPosition(BOARD_CARD_W / 2 - 0.12, BOARD_CARD_H / 2 - 0.06, 0.05)
        for (const mi of tag.render?.meshInstances ?? []) mi.material = this.blockedTagMat
        root.addChild(tag)
      }

      this.cardsHolder.addChild(root)
      this.cardEntries.push({ card, root })
    }
  }

  // ── Polling (gated on being inside the room) ────────────────────────────────

  /** Called from the main loop with whether the player is inside the board room. */
  frame(active: boolean): void {
    if (active !== this.active) {
      this.active = active
      this.container.style.display = active ? 'block' : 'none'
      if (!active) {
        this.nearestLaunchTaskId = null
        for (const div of this.cardLabels.values()) div.style.display = 'none'
      }
    }
    if (!active) return

    const camComp = this.cameraEntity.camera
    if (!camComp) return
    const camPos = this.cameraEntity.getPosition()

    interface Framed { taskId: string; sx: number; sy: number; dist: number }
    const framed: Framed[] = []

    for (const { card } of this.cardEntries) {
      const wx = card.x
      const wy = card.y
      const wz = card.z + BOARD_ISLAND_Z // local → world (root carries the offset)
      const dx = camPos.x - wx
      const dz = camPos.z - wz
      const dist = Math.sqrt(dx * dx + dz * dz)
      if (dist >= CARD_LABEL_DIST) continue

      const screen = computeLabelScreenPos(
        wx, wy + BOARD_CARD_H / 2 + 0.1, wz,
        (wv, out) => camComp.worldToScreen(wv, out),
        this.tmpVec,
      )
      if (!screen) continue
      framed.push({ taskId: card.taskId, sx: screen.x, sy: screen.y, dist })
    }

    const launchStates = useArenaStore.getState().subjectLaunchesByTaskId
    this.nearestLaunchTaskId = selectBoardLaunchTarget(
      framed.map((candidate) => ({
        taskId: candidate.taskId,
        canLaunch: this.taskById.get(candidate.taskId)?.canLaunch ?? false,
        worldDistance: candidate.dist,
        screenX: candidate.sx,
        screenY: candidate.sy,
        commandStatus: launchStates[candidate.taskId]?.status,
      })),
      this.app.graphicsDevice.width,
      this.app.graphicsDevice.height,
    )

    const visible = new Set<string>()
    for (const f of framed) {
      visible.add(f.taskId)
      const div = this.getOrCreateLabel(f.taskId)
      div.style.display = 'block'
      div.style.left = `${f.sx}px`
      div.style.top = `${f.sy}px`
      div.style.transform = 'translate(-50%, -100%)'
      div.innerHTML = this.cardHtml(f.taskId)
    }
    for (const [taskId, div] of this.cardLabels) {
      if (!visible.has(taskId)) div.style.display = 'none'
    }
  }

  private cardHtml(taskId: string): string {
    const task = this.taskById.get(taskId)
    if (!task) return ''
    const status = normalizeTaskStatus(task.status)
    let html = '<div class="wcard">'
    html += `<div class="wcard-title">${escapeWorldHtml(task.id)} · ${escapeWorldHtml(task.title)}</div>`
    html += '<div class="wcard-meta">'
    html += `<span class="wcard-pill" style="background:${STATUS_HEX[status]}">${escapeWorldHtml(status.toUpperCase())}</span>`
    if (task.priority) html += `<span>${escapeWorldHtml(task.priority.toUpperCase())}</span>`
    if (task.assignee) html += `<span>@${escapeWorldHtml(task.assignee)}</span>`
    html += '</div>'
    if (task.labels && task.labels.length > 0) {
      html += `<div class="wcard-meta">${task.labels.map((l) => `#${escapeWorldHtml(l)}`).join(' ')}</div>`
    }
    const launchState = useArenaStore.getState().subjectLaunchesByTaskId[taskId]
    if (launchState?.status === 'pending') {
      html += '<div class="wcard-kbd-row" role="status" aria-live="polite">LAUNCHING…</div>'
    } else if (launchState?.status === 'accepted') {
      html += launchState.observed
        ? `<div class="wcard-kbd-row" role="status">RUN ${escapeWorldHtml(launchState.runId)} LIVE</div>`
        : '<div class="wcard-kbd-row" role="status" aria-live="polite">ACCEPTED · SYNCING</div>'
    } else if (launchState?.status === 'failed') {
      html += `<div class="wcard-meta" role="alert">LAUNCH ${escapeWorldHtml(launchState.reason.toUpperCase())}</div>`
    }
    if (this.nearestLaunchTaskId === taskId && task.canLaunch) {
      html += '<div class="wcard-kbd-row"><span class="wcard-kbd">E</span> Launch authorized work</div>'
    }
    html += '</div>'
    return html
  }

  private readonly onKeyDown = (event: KeyboardEvent): boolean => {
    if (event.code !== 'KeyE' || !this.active) return false
    const state = useArenaStore.getState()
    if (!state.isPlaying || state.activeInteraction !== null) return false
    const taskId = this.nearestLaunchTaskId
    if (!taskId || !this.taskById.get(taskId)?.canLaunch) return false
    const launchState = state.subjectLaunchesByTaskId[taskId]
    if (launchState?.status === 'pending' || launchState?.status === 'accepted') return false
    void state.launchTaskRun(taskId)
    return true
  }

  private getOrCreateLabel(taskId: string): HTMLDivElement {
    let div = this.cardLabels.get(taskId)
    if (!div) {
      div = document.createElement('div')
      Object.assign(div.style, {
        position: 'absolute',
        fontFamily: 'ui-monospace, monospace',
        fontSize: '12px',
        userSelect: 'none',
      })
      this.container.appendChild(div)
      this.cardLabels.set(taskId, div)
    }
    return div
  }

  destroy(): void {
    this.disposeInput?.()
    this.disposeInput = undefined
    this.unsubscribe?.()
    this.unsubscribe = undefined
    for (const entry of this.cardEntries) entry.root.destroy()
    this.cardEntries = []
    this.overlay?.dispose()
    this.overlay = null
    this.cardLabels.clear()
    this.panelMats.clear()
    if (this.taskWallBatchGroupId !== undefined) {
      this.app.batcher?.removeGroup(this.taskWallBatchGroupId)
      this.taskWallBatchGroupId = undefined
    }
  }
}
